import { CommonModule } from '@angular/common';
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription, firstValueFrom } from 'rxjs';
import { distinctUntilChanged, skip, switchMap } from 'rxjs/operators';
import { CustomImportApiService, CustomImportDto, ExportIdTypeDto, formatExportIdTypeLabel } from '../../services/custom-import-api.service';
import { AuthService, SessionUser } from '../../services/auth.service';
import { NotificationService } from '../../services/notification.service';
import { CustomImportView, CustomImportStatus } from './custom-imports.types';
import { CustomImportsFormComponent } from './create/custom-imports-form.component';
import { EntityOwnerService } from '../../services/entity-owner.service';
import { CreationStateService } from '../../services/creation-state.service';
import { loadSearch, saveSearch } from '../../utils/persisted-search';
import { SearchClearDirective } from '../shared/search-clear/search-clear.directive';

@Component({
  selector: 'app-custom-imports',
  standalone: true,
  imports: [CommonModule, TranslateModule, CustomImportsFormComponent, SearchClearDirective],
  templateUrl: './custom-imports.component.html',
  styleUrls: ['./custom-imports.component.scss']
})
export class CustomImportsComponent implements OnInit, OnDestroy {
  private readonly subscriptions = new Subscription();

  customImports: CustomImportView[] = [];
  filteredImports: CustomImportView[] = [];
  exportIdTypes: ExportIdTypeDto[] = [];

  readonly statusColors: Record<CustomImportStatus, string> = {
    active: '#22c55e',
    inactive: '#9ca3af'
  };

  readonly availableStatuses: { id: CustomImportStatus; labelKey: string }[] = [
    { id: 'active', labelKey: 'customImports.filters.status.active' },
    { id: 'inactive', labelKey: 'customImports.filters.status.inactive' }
  ];

  isLoading = false;
  loadingError: string | null = null;
  searchTerm = loadSearch('reach/custom-imports');
  statusFilters: Record<CustomImportStatus, boolean> = this.createStatusFilters();
  pageSizeOptions = [10, 25, 50];
  pageSize = 10;
  currentPage = 0;
  readonly locale = 'it-IT';

  panelOpen = false;
  formMode: 'create' | 'edit' = 'create';
  editingImport: CustomImportView | null = null;

  currentUser: SessionUser | null = null;
  // Advanced (Console/admin) mode — set from route data on /console/custom-imports. Unlocks the GCS
  // import source in the form. Defaults to the simplified user experience.
  advanced = false;
  private ownerLabels = new Map<string, string>();
  activeActionMenuId: string | null = null;
  activeActionItem: CustomImportView | null = null;
  actionMenuPosition = { top: 0 as number | null, bottom: null as number | null, left: 0 };
  actionMenuArrowOffset = 24;
  actionMenuOpenUpward = false;

  constructor(
    private readonly customImportApi: CustomImportApiService,
    private readonly translate: TranslateService,
    private readonly authService: AuthService,
    private readonly notificationService: NotificationService,
    private readonly entityOwnerService: EntityOwnerService,
    private readonly creationStateService: CreationStateService,
    private readonly route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.advanced = this.route.snapshot.data['advanced'] === true;
    this.subscriptions.add(
      this.authService.currentUser$.subscribe(user => {
        this.currentUser = user;
      })
    );
    this.subscriptions.add(
      this.entityOwnerService.owners$.subscribe(map => {
        this.ownerLabels = map;
      })
    );
    this.subscriptions.add(
      this.authService.selectedDcr$
        .pipe(distinctUntilChanged(), skip(1), switchMap(() => this.authService.waitForEntitySync()))
        .subscribe(() => {
          this.loadExportIdTypes();
          this.loadCustomImports();
        })
    );

    this.authService.waitForEntitySync().subscribe(() => {
      this.loadExportIdTypes();
      this.loadCustomImports();
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    // Ensure entity selector is always re-enabled when component is destroyed
    this.creationStateService.setCreationMode(false);
  }

  @HostListener('document:click', ['$event'])
  handleDocumentClick(event: MouseEvent): void {
    const target = event.target instanceof HTMLElement ? event.target : null;
    const clickedMenu = !!target?.closest('.custom-imports__action-menu');
    const clickedTrigger = !!target?.closest('.custom-imports__action-trigger');
    if (!clickedMenu && !clickedTrigger) {
      this.closeActionMenu();
    }
  }

  loadExportIdTypes(): void {
    const sub = this.customImportApi.getExportIdTypes().subscribe({
      next: (types) => {
        // Simplified (user) mode is limited to the common ID types; advanced (Console) mode
        // exposes every type returned by the backend (as the old admin dashboard did).
        // The ExportIDType VALUE is kept exactly as the backend returns it (it must be sent back
        // unchanged on save — casing matters server-side). `Name` stays the technical code; the
        // human display uses formatExportIdTypeLabel separately (dropdown label + list rows).
        // Allow-list in the backend's canonical (lowercase) casing; matched case-insensitively.
        const allowed = new Set(['email_md5', 'email_sha256', 'vat_id', 'maid', 'tncid', 'crmid']);
        const decorated = types.map(type => ({
          ...type,
          Name: type.ExportIDType.toUpperCase()
        }));
        const filtered = this.advanced
          ? decorated
          : decorated.filter(type => allowed.has(type.ExportIDType.toLowerCase()));
        this.exportIdTypes = filtered.sort((a, b) => a.Name.localeCompare(b.Name));
      },
      error: (error) => {
        console.error('[custom-imports] export ID types load failed', error);
        this.exportIdTypes = [];
      }
    });
    this.subscriptions.add(sub);
  }

  loadCustomImports(): void {
    this.isLoading = true;
    this.loadingError = null;
    const sub = this.customImportApi.getCustomImports().subscribe({
      next: (list) => {
        const mapped = list
          .map(dto => this.mapCustomImport(dto))
          .sort((a, b) => b.creationDateRaw.localeCompare(a.creationDateRaw));
        this.entityOwnerService.ensureOwners(mapped.map(i => ({ id: i.ownerEntityId })));
        this.customImports = [...this.filterImportsByDcr(mapped)];
        this.resetPagination();
        this.applyFilters();
        this.isLoading = false;
      },
      error: (error) => {
        console.error('[custom-imports] list load failed', error);
        this.isLoading = false;
        this.loadingError = this.translate.instant('customImports.errors.load');
        this.customImports = [];
        this.filteredImports = [];
      }
    });
    this.subscriptions.add(sub);
  }

  onSearch(term: string): void {
    this.searchTerm = term;
    saveSearch('reach/custom-imports', term);
    this.resetPagination();
    this.applyFilters();
  }

  get paginatedImports(): CustomImportView[] {
    const startIndex = this.currentPage * this.pageSize;
    return this.filteredImports.slice(startIndex, startIndex + this.pageSize);
  }

  get totalPages(): number {
    if (!this.filteredImports.length) {
      return 1;
    }
    return Math.max(1, Math.ceil(this.filteredImports.length / this.pageSize));
  }

  changePageSize(size: Event | string | number): void {
    let numeric: number;
    if (size instanceof Event) {
      const target = size.target as HTMLSelectElement | null;
      numeric = target ? parseInt(target.value, 10) : this.pageSize;
    } else if (typeof size === 'string') {
      numeric = parseInt(size, 10);
    } else {
      numeric = size;
    }

    if (!Number.isFinite(numeric) || numeric <= 0 || numeric === this.pageSize) {
      return;
    }
    this.pageSize = numeric;
    this.resetPagination();
    this.applyFilters();
  }

  goToPage(page: number): void {
    if (page < 0 || page >= this.totalPages) {
      return;
    }
    this.currentPage = page;
  }

  clearSearch(): void {
    if (!this.searchTerm) {
      return;
    }
    this.searchTerm = '';
    saveSearch('reach/custom-imports', '');
    this.resetPagination();
    this.applyFilters();
  }

  toggleStatusFilter(status: CustomImportStatus): void {
    this.statusFilters = {
      ...this.statusFilters,
      [status]: !this.statusFilters[status]
    };
    this.resetPagination();
    this.applyFilters();
  }

  get statusCounts(): Record<CustomImportStatus, number> {
    const normalizedSearch = this.searchTerm.toLowerCase().trim();
    const counts: Record<CustomImportStatus, number> = {
      active: 0,
      inactive: 0
    };
    for (const customImport of this.customImports) {
      if (this.matchesSearch(customImport, normalizedSearch)) {
        counts[customImport.status] += 1;
      }
    }
    return counts;
  }

  private matchesSearch(item: CustomImportView, normalizedSearch: string): boolean {
    if (!normalizedSearch) {
      return true;
    }
    const haystack = `${item.name} ${item.exportIdTypeName} ${item.owner}`.toLowerCase();
    return haystack.includes(normalizedSearch);
  }

  private createStatusFilters(): Record<CustomImportStatus, boolean> {
    return {
      active: true,
      inactive: false
    };
  }

  get hasGlobalDataScope(): boolean {
    return this.authService.hasGlobalDataScope();
  }

  get ownerEntityOptions(): { value: string; label: string }[] {
    const dcrs = this.currentUser?.availableDCRs ?? [];
    return dcrs.map(dcr => ({
      value: dcr.id,
      label: dcr.Name || dcr.name || dcr.id
    }));
  }

  openCreatePanel(): void {
    this.closeActionMenu();
    this.formMode = 'create';
    this.editingImport = null;
    this.panelOpen = true;
    // Notify sidebar to disable entity selector
    this.creationStateService.setCreationMode(true);
  }

  openEditPanel(item: CustomImportView): void {
    this.closeActionMenu();
    this.formMode = 'edit';
    this.editingImport = item;
    this.panelOpen = true;
    // Notify sidebar to disable entity selector
    this.creationStateService.setCreationMode(true);
  }

  closePanel(): void {
    this.panelOpen = false;
    this.editingImport = null;
    // Notify sidebar to enable entity selector
    this.creationStateService.setCreationMode(false);
  }

  handleFormSaved(): void {
    this.panelOpen = false;
    this.editingImport = null;
    // Notify sidebar to enable entity selector
    this.creationStateService.setCreationMode(false);
    this.loadCustomImports();
  }

  copiedImportId: string | null = null;

  copyId(id: string, event: Event): void {
    event.stopPropagation();
    navigator.clipboard?.writeText(id).then(() => {
      this.copiedImportId = id;
      setTimeout(() => {
        if (this.copiedImportId === id) {
          this.copiedImportId = null;
        }
      }, 1500);
    });
  }

  async toggleStatus(item: CustomImportView): Promise<void> {
    const nextStatus: CustomImportStatus = item.status === 'active' ? 'inactive' : 'active';
    const apiStatus = nextStatus === 'active' ? 1 : 0;
    try {
      await firstValueFrom(this.customImportApi.updateStatus(item.id, apiStatus));
      item.status = nextStatus;
      const messageKey = nextStatus === 'active'
        ? 'customImports.messages.activated'
        : 'customImports.messages.deactivated';
      this.notificationService.notify('success', this.translate.instant(messageKey));
      // Deliberately not re-running applyFilters() here: with the default status filter
      // (active only), toggling a visible row to inactive would immediately remove it from
      // filteredImports/paginatedImports, destroying the row (and its switch) before the
      // toggle's own visual state ever settles — it looks like "the switch doesn't turn off".
      // Leave the row in place until the user changes filters/search/reloads themselves.
    } catch (error) {
      console.error('[custom-imports] status toggle failed', error);
      this.notificationService.notify('error', this.translate.instant('customImports.errors.status'));
    }
    this.closeActionMenu();
  }

  toggleActionsMenu(item: CustomImportView, event: Event): void {
    event.stopPropagation();
    const isOpen = this.activeActionMenuId === item.id;
    if (isOpen) {
      this.closeActionMenu();
      return;
    }
    this.activeActionMenuId = item.id;
    this.activeActionItem = item;
    this.updateActionMenuPosition(event.currentTarget as HTMLElement);
  }

  private applyFilters(): void {
    const term = this.searchTerm.trim().toLowerCase();
    this.filteredImports = this.customImports.filter(item => {
      const matchesTerm = !term ||
        item.name.toLowerCase().includes(term) ||
        item.exportIdTypeName.toLowerCase().includes(term) ||
        item.owner.toLowerCase().includes(term);
      const matchesStatus = this.statusFilters[item.status];
      return matchesTerm && matchesStatus;
    });
    this.ensureValidPage();
  }

  private resetPagination(): void {
    this.currentPage = 0;
  }

  private ensureValidPage(): void {
    if (!this.filteredImports.length) {
      this.currentPage = 0;
      return;
    }
    const totalPages = Math.max(1, Math.ceil(this.filteredImports.length / this.pageSize));
    const lastPageIndex = Math.max(0, totalPages - 1);
    if (this.currentPage > lastPageIndex) {
      this.currentPage = lastPageIndex;
    }
  }

  private mapCustomImport(dto: CustomImportDto): CustomImportView {
    // OwnerEntityType is no longer sent by the backend (it was always the hardcoded
    // constant "publisher" server-side, never actually varied) — kept here as a constant
    // so the owner-label cache key (EntityOwnerService, keyed "publisher::{id}") still resolves.
    const ownerLabel = this.resolveOwnerLabel(dto.OwnerEntityID, 'publisher');
    return {
      id: dto.ID,
      name: dto.Name,
      owner: ownerLabel,
      ownerEntityType: 'publisher',
      ownerEntityId: dto.OwnerEntityID,
      exportIdType: dto.ExportIDType,
      exportIdTypeName: formatExportIdTypeLabel(dto.ExportIDType),
      creationDate: this.formatDate(dto.CreationTime),
      creationDateRaw: dto.CreationTime,
      status: this.normalizeStatus(dto.Status ?? 0),
      exportIdCount: dto.ExportIDCount ?? 0,
      tncIdsCount: dto.TNCIDsCount ?? 0,
      storagePath: dto.StorageFilePath
    };
  }

  private filterImportsByDcr(imports: CustomImportView[]): CustomImportView[] {
    const filterId = this.getActiveDcrFilter();
    if (!filterId) {
      return imports;
    }
    return imports.filter(item => this.normalizeEntityId(item.ownerEntityId) === filterId);
  }

  /** Single source of truth for the entity-selector scope (see AuthService.getEntityScopeFilterId). */
  private getActiveDcrFilter(): string | null {
    return this.authService.getEntityScopeFilterId();
  }

  private normalizeEntityId(value?: string | null): string {
    return (value ?? '').toString().trim().toLowerCase();
  }

  private normalizeStatus(status: number): CustomImportStatus {
    return status === 1 ? 'active' : 'inactive';
  }

  private formatDate(value: string): string {
    if (!value) {
      return '';
    }
    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) {
      return value;
    }
    const locale = this.locale;
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(parsedDate);
  }
  private resolveOwnerLabel(entityId?: string | null, entityType?: string | null): string {
    const key = this.buildOwnerKey(entityType, entityId);
    if (key) {
      const mapped = this.ownerLabels.get(key);
      if (mapped) {
        return mapped;
      }
    }
    const normalizedId = (entityId ?? '').toString().trim();
    if (normalizedId) {
      const friendly = this.authService.getDcrNameById(normalizedId);
      if (friendly) {
        return friendly;
      }
    }
    if (normalizedId && entityType) {
      return `${entityType} • ${normalizedId}`;
    }
    return normalizedId || entityType || '-';
  }

  private buildOwnerKey(entityType?: string | null, entityId?: string | null): string | null {
    const type = (entityType ?? '').toString().trim().toLowerCase();
    const id = (entityId ?? '').toString().trim();
    if (!type || !id) {
      return null;
    }
    return `${type}::${id}`;
  }

  private closeActionMenu(): void {
    this.activeActionMenuId = null;
    this.activeActionItem = null;
    this.actionMenuArrowOffset = 24;
  }

  private updateActionMenuPosition(trigger: HTMLElement): void {
    if (typeof window === 'undefined') {
      this.actionMenuPosition = { top: 0, bottom: null, left: 0 };
      this.actionMenuArrowOffset = 24;
      this.actionMenuOpenUpward = false;
      return;
    }
    const rect = trigger.getBoundingClientRect();
    const menuWidth = 240;
    const menuHeight = 140;
    const padding = 16;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    const preferredLeft = rect.right - menuWidth;
    const maxLeft = viewportWidth - menuWidth - padding;
    const left = Math.min(Math.max(preferredLeft, padding), maxLeft);

    const spaceBelow = viewportHeight - rect.bottom;
    const openUpward = spaceBelow < menuHeight + 16;
    this.actionMenuOpenUpward = openUpward;

    this.actionMenuPosition = openUpward
      ? { top: null, bottom: viewportHeight - rect.top + 8, left }
      : { top: rect.bottom + 8, bottom: null, left };

    const buttonCenter = rect.left + rect.width / 2;
    const arrowPadding = 24;
    const relativeCenter = buttonCenter - left;
    const clampedOffset = Math.min(Math.max(relativeCenter, arrowPadding), menuWidth - arrowPadding);
    this.actionMenuArrowOffset = clampedOffset;
  }

}
